import type { Session, UserData, UUID } from '@cobliteam/common-types';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

export interface AuthState {
  session: Session | null;
  /** `userId` dono do `userData` cacheado — evita usar dados de outro usuário após troca de sessão. */
  userId: UUID | null;
  userData: UserData | null;

  setSession: (session: Session) => void;
  updateSession: (patch: Partial<Session>) => void;
  clearSession: () => void;

  setUserData: (userId: UUID, userData: UserData) => void;
  clearUserData: () => void;

  /** Reset atômico usado por `useLogout` — limpa session e userData num único write. */
  reset: () => void;
}

type PersistedAuthState = Pick<AuthState, 'session' | 'userId' | 'userData'>;

const initialState: PersistedAuthState = {
  session: null,
  userId: null,
  userData: null,
};

const AUTH_STORAGE_KEY = 'cobli-auth';

/**
 * Fonte única para estado de autenticação persistido (session + userData snapshot). Substituiu
 * `useSessionStore` + `useUserDataStore`. Tokens continuam em `libs/api-lib` (AuthToken.*).
 *
 * A revalidação em background é feita pelos hooks em `modules/hooks/src/useSession/`; este store
 * é apenas o cache local otimista.
 */
const useAuthStore = create<AuthState, [['zustand/devtools', never], ['zustand/persist', PersistedAuthState]]>(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        setSession: (session) => set({ session }, false, 'auth/session/set'),
        updateSession: (patch) => set((state) => ({ session: state.session ? { ...state.session, ...patch } : state.session }), false, 'auth/session/update'),
        clearSession: () => set({ session: null }, false, 'auth/session/clear'),
        setUserData: (userId, userData) => set({ userId, userData }, false, 'auth/userData/set'),
        clearUserData: () => set({ userId: null, userData: null }, false, 'auth/userData/clear'),
        reset: () => set(initialState, false, 'auth/reset'),
      }),
      {
        name: AUTH_STORAGE_KEY,
        storage: createJSONStorage(() => localStorage),
        version: 1,
        partialize: (state) => ({ session: state.session, userId: state.userId, userData: state.userData }),
        // Se o shape mudar, descarta cache e força revalidação via `/account/session` + `userData`.
        migrate: () => initialState,
      }
    ),
    { name: 'useAuthStore' }
  )
);

export default useAuthStore;
